/**
 * Ask Dink assistant wrapper (dink-api DNKAPI-0627, DNKADM-0434).
 *
 * Contract (bearer `/v2/admin/**` surface via `request()` — no CSRF, no cookies;
 * the tenant comes from the JWT, never the body):
 *  - `POST /v2/admin/assistant/ask`
 *    `{ question (1..1000), routes: [{ path, title, description? }] (≤300), history?: [{ role, text }] (≤6) }`
 *    → `{ answer (markdown), links: [{ path, label }] (0..3), questionId, kind, sources,
 *    suggestions }`. DNKAPI-0673 (DNKADM-0460) added the last three: `kind` is `help` or
 *    `unsure` (`facility` is reserved, not returned yet); `sources` names up to 3 help-guide
 *    sections (always `[]` for `unsure`); `suggestions` is 0..3 follow-up questions, already
 *    filtered to the caller's role. On `unsure`, `answer` is one line naming where Dink looked.
 *  - `POST /v2/admin/assistant/questions/{id}/feedback` `{ helpful }`.
 *  - `GET /v2/admin/assistant/status` -> `{ enabled }` (DNKAPI-0665, DNKADM-0455): the
 *    global AND tenant switches. The launcher shows the button only on `enabled === true`.
 *  - Errors: `404 ASSISTANT_DISABLED` (switched off), `503 ASSISTANT_UNAVAILABLE`
 *    (provider down / timeout), `429 ASSISTANT_RATE_LIMITED` (over the limit) — read
 *    with {@link classifyAssistantError}.
 *
 * The response parse is defensive: a missing or wrong-typed `answer`/`questionId`
 * throws (the caller shows the unavailable state), and malformed `links`, `sources` and
 * `suggestions` entries are dropped rather than rendered. An older API that sends only
 * `{ answer, links, questionId }` still parses: no `kind` reads as `help` (the only kind
 * that API had), no `sources`/`suggestions` as `[]`. A `kind` this client does not know
 * (`facility` included) reads as `other`, which the panel shows without a source tag
 * rather than mislabel it as the help guide. Nothing here is stored — questions, answers and
 * history live only in the panel's component state.
 */
import { request } from './client';
import { ApiError } from './errors';
import { v2ErrorCode } from './v2Errors';

/** The contract's caps (DNKAPI-0627 U1). */
export const ASSISTANT_QUESTION_MAX = 1000;
/**
 * History cap. dink-api defines a TURN as one question + answer pair, so 6 turns are
 * 12 `{ role, text }` messages (DNKAPI-0627 `AssistantService.MAX_HISTORY_MESSAGES`).
 */
export const ASSISTANT_HISTORY_TURNS = 6;
export const ASSISTANT_HISTORY_MESSAGES = ASSISTANT_HISTORY_TURNS * 2;

/** One prior message sent back for follow-ups. */
export interface AssistantTurn {
  role: 'user' | 'assistant';
  text: string;
}

/** One screen the caller's console can open (built by `features/ask-dink/routeCatalogue`). */
export interface AssistantRoute {
  path: string;
  title: string;
  description?: string;
}

export interface AskRequest {
  question: string;
  routes: AssistantRoute[];
  history?: AssistantTurn[];
}

export interface AssistantLink {
  path: string;
  label: string;
}

/** Where an answer came from. `other` is any kind this client does not know yet. */
export type AssistantAnswerKind = 'help' | 'unsure' | 'other';

export interface AskResponse {
  answer: string;
  links: AssistantLink[];
  questionId: string;
  kind: AssistantAnswerKind;
  /** Help-guide section names the answer used (0..3). */
  sources: string[];
  /** Follow-up questions to offer (0..3). */
  suggestions: string[];
}

/** The contract's caps on `sources` and `suggestions` (DNKAPI-0673). */
const MAX_SOURCES = 3;
const MAX_SUGGESTIONS = 3;

function parseLinks(raw: unknown): AssistantLink[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item: unknown) => {
    if (typeof item !== 'object' || item === null) return [];
    const { path, label } = item as Record<string, unknown>;
    return typeof path === 'string' && typeof label === 'string' ? [{ path, label }] : [];
  });
}

function parseKind(raw: unknown): AssistantAnswerKind {
  if (raw === undefined || raw === null || raw === 'help') return 'help';
  return raw === 'unsure' ? 'unsure' : 'other';
}

/** Non-blank strings only, trimmed, at most `max`. */
function parseStrings(raw: unknown, max: number): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .flatMap((item: unknown) =>
      typeof item === 'string' && item.trim() !== '' ? [item.trim()] : [],
    )
    .slice(0, max);
}

/**
 * Ask one question. Throws `ApiError` on a non-2xx, or `Error` on a malformed body.
 * `signal` cancels the request (the panel's Stop button).
 */
export async function askAssistant(
  body: AskRequest,
  options: { signal?: AbortSignal } = {},
): Promise<AskResponse> {
  const res = await request<unknown>('POST', '/v2/admin/assistant/ask', {
    json: body,
    ...(options.signal !== undefined ? { signal: options.signal } : {}),
  });
  const obj = typeof res === 'object' && res !== null ? (res as Record<string, unknown>) : {};
  const { answer, questionId } = obj;
  if (
    typeof answer !== 'string' ||
    (typeof questionId !== 'string' && typeof questionId !== 'number')
  ) {
    throw new Error('Malformed assistant response');
  }
  return {
    answer,
    links: parseLinks(obj['links']),
    questionId: String(questionId),
    kind: parseKind(obj['kind']),
    sources: parseStrings(obj['sources'], MAX_SOURCES),
    suggestions: parseStrings(obj['suggestions'], MAX_SUGGESTIONS),
  };
}

/**
 * Is the assistant on for this session? Only a literal `enabled: true` counts; any other
 * body shape reads as off. Throws `ApiError` on a non-2xx, so the caller fails closed.
 */
export async function getAssistantStatus(): Promise<boolean> {
  const res = await request<unknown>('GET', '/v2/admin/assistant/status');
  return (
    typeof res === 'object' && res !== null && (res as Record<string, unknown>)['enabled'] === true
  );
}

/** Record thumbs up/down on one answer. */
export async function sendAssistantFeedback(questionId: string, helpful: boolean): Promise<void> {
  await request<unknown>(
    'POST',
    `/v2/admin/assistant/questions/${encodeURIComponent(questionId)}/feedback`,
    { json: { helpful } },
  );
}

/** How the panel reacts to a failed ask. */
export type AssistantFailure = 'disabled' | 'rateLimited' | 'unavailable';

/**
 * Classify a failed ask. Only the contract's `404 ASSISTANT_DISABLED` hides the
 * assistant — a bare `404` (no code) is treated as unavailable, so a missing route
 * never silently removes the button. `429` is the rate limit with or without its
 * code; everything else (503, network, malformed body) is unavailable.
 */
export function classifyAssistantError(err: unknown): AssistantFailure {
  const code = v2ErrorCode(err);
  if (code === 'ASSISTANT_DISABLED') return 'disabled';
  if (code === 'ASSISTANT_RATE_LIMITED') return 'rateLimited';
  if (err instanceof ApiError && err.status === 429) return 'rateLimited';
  return 'unavailable';
}
